/**
 * ContributionPanel — the AI-first workflow surface.
 *
 * For MEMBERS: a single "Upload your contribution" card. After upload it
 * shows the AI-classified section(s) with confidence. Low-confidence uploads
 * or wrong-document uploads bring up a picker to choose the correct section.
 *
 * For OWNERS: a project-tracker showing brief-section coverage — how many
 * sections are covered, missing, or flagged for review. The old manual
 * assignment UI is still available further down the page under an
 * "Advanced options" toggle.
 */
import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  contribute, uploadFile, errorMessage,
} from "../../lib/api";
import {
  Upload, Loader2, CheckCircle2, X, AlertTriangle, Sparkles,
  RefreshCw, FileText, Copy, Link as LinkIcon,
} from "lucide-react";

const ContributionOutcome = ({ result, onDismiss }) => {
  if (!result) return null;
  const wrongDoc = result.wrong_document;
  const needs = result.needs_confirmation;
  const conf = result.confidence;
  if (wrongDoc) {
    return (
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="contribution-wrong-doc">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-900 text-sm">Are you sure you uploaded the correct file?</p>
            <p className="text-xs text-amber-900 mt-1">{result.wrong_document_reason}</p>
          </div>
          <button onClick={onDismiss} className="text-amber-500 hover:text-amber-700 p-0.5" aria-label="Dismiss"><X size={14} /></button>
        </div>
      </motion.div>
    );
  }
  if (needs) return null;   // picker rendered separately
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="contribution-success">
      <div className="flex items-start gap-3">
        <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-emerald-900 text-sm">
            Upload received.
          </p>
          <p className="text-xs text-emerald-900 mt-1">
            MyAE identified this as <b>{result.section_title}</b>
            {typeof conf === "number" && conf >= 95 && <> — {conf}% confident, no further action needed.</>}
            {typeof conf === "number" && conf < 95 && <> — {conf}% confident, flagged for owner review.</>}
          </p>
        </div>
        <button onClick={onDismiss} className="text-emerald-500 hover:text-emerald-700 p-0.5" aria-label="Dismiss"><X size={14} /></button>
      </div>
    </motion.div>
  );
};

const SectionPicker = ({ result, onPick, onCancel, busy }) => {
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 mt-3" data-testid="contribution-picker">
      <p className="font-medium text-indigo-900 text-sm mb-1">
        {result.wrong_document
          ? "If this is intentional, pick where it belongs:"
          : "We couldn&rsquo;t confidently determine which section this document belongs to. Please select the correct section:"}
      </p>
      <p className="text-xs text-indigo-800 mb-3">Your upload wasn&rsquo;t sent yet. Choose the correct section and MyAE will submit it.</p>
      <div className="space-y-2">
        {(result.options || []).map((opt) => (
          <button
            key={opt.section_id}
            onClick={() => onPick(opt.section_id)}
            disabled={busy}
            data-testid={`picker-option-${opt.section_id}`}
            className="w-full text-left rounded-lg bg-white border border-zinc-200 hover:border-indigo-400 hover:shadow-sm px-3 py-2 disabled:opacity-60"
          >
            <p className="font-medium text-sm text-zinc-900">{opt.title}</p>
            {opt.description && <p className="text-xs text-zinc-500 mt-0.5">{opt.description}</p>}
          </button>
        ))}
      </div>
      <button onClick={onCancel} className="mt-3 text-xs text-zinc-500 hover:text-zinc-800">Cancel &amp; re-upload</button>
    </div>
  );
};

export const CoverageTracker = ({ grp }) => {
  const sections = grp.sections || [];
  const uploaded = sections.filter((s) => s.status === "uploaded" || s.status === "analyzed").length;
  const total = sections.length;
  const missing = sections.filter((s) => s.status === "pending");
  const reviewFlagged = sections.filter((s) => s.review_recommended);
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="coverage-tracker">
      <div className="flex items-center justify-between mb-3">
        <p className="font-display text-base font-semibold text-zinc-900">Project tracker</p>
        <p className="text-xs text-zinc-500 tabular-nums">{uploaded} of {total} sections</p>
      </div>
      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden mb-4">
        <div className="h-full bg-indigo-600 transition-all" style={{ width: total ? `${(uploaded/total)*100}%` : "0%" }} />
      </div>
      <div className="space-y-1.5" data-testid="coverage-list">
        {sections.sort((a,b) => a.order - b.order).map((s) => {
          const done = s.status === "uploaded" || s.status === "analyzed";
          const review = s.review_recommended;
          return (
            <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`coverage-row-${s.id}`}>
              <span className={`w-4 shrink-0 ${done ? "text-emerald-600" : "text-amber-500"}`}>
                {done ? "✓" : "⚠"}
              </span>
              <span className={`flex-1 ${done ? "text-zinc-800" : "text-zinc-600"}`}>{s.title}</span>
              {review && <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">Review</span>}
              {s.assigned_to_name && <span className="text-xs text-zinc-500 truncate max-w-[120px]">{s.assigned_to_name}</span>}
            </div>
          );
        })}
        {missing.length > 0 && (
          <p className="text-xs text-zinc-500 pt-2 border-t border-zinc-100">
            <b>{missing.length}</b> section{missing.length === 1 ? "" : "s"} still needed
            {reviewFlagged.length > 0 && <> · <b>{reviewFlagged.length}</b> flagged for review</>}
          </p>
        )}
        {missing.length === 0 && total > 0 && (
          <p className="text-xs text-emerald-700 pt-2 border-t border-zinc-100">All sections received. Ready for analysis.</p>
        )}
      </div>
    </div>
  );
};

export const InviteLinkPanel = ({ url, joined, max }) => {
  if (!url) return null;
  const full = joined >= max;
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Invite link copied"); };
  return (
    <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5" data-testid="invite-link-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-display text-base font-semibold text-zinc-900 flex items-center gap-2">
            <LinkIcon size={16} className="text-indigo-600" /> One link to share with your group
          </p>
          <p className="text-xs text-zinc-600 mt-0.5">
            Everyone with this link joins the project automatically. {full ? "The group is now full." : `${joined}/${max} members joined so far.`}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input readOnly value={url} data-testid="invite-link-input"
          className="flex-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-mono text-zinc-700 focus:outline-none" />
        <button onClick={copy} data-testid="invite-link-copy"
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white px-3 py-2 text-xs font-semibold hover:bg-indigo-700">
          <Copy size={12} /> Copy link
        </button>
      </div>
    </div>
  );
};

export const MemberUploadCard = ({ groupId, hasBrief, onDone, briefTopic }) => {
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const chooseFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      setText(up.text || "");
      setFilename(file.name);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file."));
    } finally { setBusy(false); }
  };

  const submit = async (forcedSectionId = "") => {
    const words = (text.trim().match(/\b\w+\b/g) || []).length;
    if (words < 30) return toast.error("Add at least 30 words for a meaningful analysis.");
    setBusy(true);
    try {
      const r = await contribute(groupId, {
        text: text.trim(),
        filename: filename || "Contribution.txt",
        forced_section_id: forcedSectionId,
      });
      setResult(r);
      if (r.assigned) {
        // Reset input for potential next upload
        setText("");
        setFilename("");
        onDone && onDone();
      }
    } catch (e) {
      toast.error(errorMessage(e, "Could not submit your contribution."));
    } finally { setBusy(false); }
  };

  const dismiss = () => setResult(null);
  const cancelPicker = () => { setResult(null); };
  const pickSection = (sectionId) => submit(sectionId);

  if (!hasBrief) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="upload-waiting-brief">
        <p className="text-sm text-amber-900 font-medium">Waiting for the owner to upload the assignment brief.</p>
        <p className="text-xs text-amber-900 mt-1">Once the brief is loaded, you&rsquo;ll be able to upload your contribution here and MyAE will automatically identify which section it covers.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-indigo-200 bg-white p-5 space-y-4" data-testid="member-upload-card">
      <div>
        <p className="font-display text-base font-semibold text-zinc-900 flex items-center gap-2">
          <Upload size={16} className="text-indigo-600" /> Upload your contribution
        </p>
        <p className="text-xs text-zinc-500 mt-0.5">
          {briefTopic
            ? <>MyAE will identify which section of &ldquo;{briefTopic}&rdquo; your work covers &mdash; no need to pick.</>
            : "Just upload your work. MyAE will figure out which section it belongs to."}
        </p>
      </div>

      <AnimatePresence mode="wait">
        {result && (
          <div key="outcome">
            <ContributionOutcome result={result} onDismiss={dismiss} />
            {result.needs_confirmation && (
              <SectionPicker result={result} onPick={pickSection} onCancel={cancelPicker} busy={busy} />
            )}
          </div>
        )}
      </AnimatePresence>

      {(!result || !result.needs_confirmation) && (
        <>
          <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.rtf,.md" className="hidden" onChange={(e) => chooseFile(e.target.files?.[0])} data-testid="member-upload-file-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="member-upload-choose-file"
            className="w-full rounded-lg border-2 border-dashed border-zinc-300 hover:border-indigo-400 py-5 text-sm text-zinc-600 flex flex-col items-center gap-2 transition-colors"
          >
            <Upload size={20} className="text-zinc-400" />
            <span>{filename ? `Selected: ${filename}` : "Click to upload your work"}</span>
          </button>
          <div className="relative flex items-center">
            <div className="flex-1 border-t border-zinc-200" />
            <span className="px-3 text-xs text-zinc-400">or paste below</span>
            <div className="flex-1 border-t border-zinc-200" />
          </div>
          <textarea
            data-testid="member-upload-text"
            className="w-full min-h-[200px] rounded-lg border border-zinc-300 p-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
            placeholder="Paste your contribution here…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-zinc-500">{(text.trim().match(/\b\w+\b/g) || []).length} words</p>
            <button
              onClick={() => submit("")}
              disabled={busy || !text.trim()}
              data-testid="member-upload-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> MyAE is reading…</> : <><Sparkles size={14} /> Submit &amp; auto-classify</>}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
